import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Send,
  Paperclip,
  Download,
  Clock,
  Tag,
  Monitor,
  MapPin,
  CalendarDays,
  AlertCircle,
  CheckCircle2,
  Image as ImageIcon,
  File as FileIcon,
  X,
  Lock,
  ChevronDown,
  ChevronUp,
  User as UserIcon,
  Pencil,
  Trash2,
  Copy,
  Headphones,
  BookOpen,
  Phone,
} from 'lucide-react';
import { ticketsApi, Ticket, TicketComment, Attachment } from '@/api/endpoints';
import { useAuthStore } from '@/stores/authStore';
import Button from '@/components/ui/Button';
import Badge, {
  getStatusBadgeVariant,
  getStatusLabel,
  getPriorityBadgeVariant,
  getPriorityLabel,
} from '@/components/ui/Badge';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import StarRating from '@/components/ui/StarRating';
import { io, Socket } from 'socket.io-client';
import clsx from 'clsx';

const getCategoryLabel = (category: string): string => {
  const map: Record<string, string> = {
    bug: 'Ошибка',
    setup: 'Настройка',
    billing: 'Биллинг',
    consultation: 'Консультация',
    feature_request: 'Запрос функции',
    incident: 'Авария',
  };
  return map[category] || category;
};

const getStaffRoleLabel = (role: string): string | null => {
  const map: Record<string, string> = {
    admin: 'Администратор',
    developer: 'Разработчик',
    operator: 'Оператор',
    senior_specialist: 'Старший специалист',
    manager: 'Менеджер',
  };
  return map[role] || null;
};

const TicketDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Контейнер чата теперь со своим overflow — раньше scrollIntoView
  // на messagesEndRef прокручивал всю страницу (контейнер не имел фикс.
  // высоты). Теперь скроллим только сам тред, страница остаётся на месте.
  const threadRef = useRef<HTMLDivElement>(null);

  const [replyText, setReplyText] = useState('');
  const [replyFiles, setReplyFiles] = useState<File[]>([]);

  // «Оператор печатает…» — приватный namespace '/presence' (без контента),
  // + отправка своего статуса набора. Подробности — в backend app.ts.
  const [operatorTyping, setOperatorTyping] = useState(false);
  const presenceSocketRef = useRef<Socket | null>(null);
  const typingClearRef = useRef<number | null>(null);      // авто-сброс входящего
  const typingStopTimerRef = useRef<number | null>(null);  // дебаунс исходящего stop
  const typingActiveRef = useRef(false);
  const typingLastEmitRef = useRef(0);                     // heartbeat-троттл

  useEffect(() => {
    if (!id) return;
    let socket: Socket | null = null;
    try {
      socket = io('/presence', {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
      });
    } catch {
      return;
    }
    const join = () => socket?.emit('join:ticket', id);
    socket.on('connect', join);
    if (socket.connected) join();
    socket.on('ticket:typing', (p: { role?: string }) => {
      if (p?.role && p.role !== 'staff') return;
      setOperatorTyping(true);
      if (typingClearRef.current) window.clearTimeout(typingClearRef.current);
      typingClearRef.current = window.setTimeout(() => setOperatorTyping(false), 4000);
    });
    socket.on('ticket:typing_stop', () => {
      if (typingClearRef.current) window.clearTimeout(typingClearRef.current);
      setOperatorTyping(false);
    });
    presenceSocketRef.current = socket;
    return () => {
      socket?.emit('leave:ticket', id);
      socket?.removeAllListeners();
      socket?.disconnect();
      presenceSocketRef.current = null;
      if (typingClearRef.current) window.clearTimeout(typingClearRef.current);
      if (typingStopTimerRef.current) window.clearTimeout(typingStopTimerRef.current);
    };
  }, [id]);

  const emitTyping = () => {
    const s = presenceSocketRef.current;
    if (!s || !id) return;
    typingActiveRef.current = true;
    // Heartbeat: переотправка не чаще раза в 1.5с, пока идёт набор, чтобы
    // индикатор у оператора не гас по 4-секундному авто-сбросу.
    const now = Date.now();
    if (now - typingLastEmitRef.current > 1500) {
      typingLastEmitRef.current = now;
      s.emit('ticket:typing', { ticketId: id, role: 'client' });
    }
    if (typingStopTimerRef.current) window.clearTimeout(typingStopTimerRef.current);
    typingStopTimerRef.current = window.setTimeout(() => {
      typingActiveRef.current = false;
      typingLastEmitRef.current = 0;
      s.emit('ticket:typing_stop', { ticketId: id });
    }, 2500);
  };

  const emitTypingStop = () => {
    const s = presenceSocketRef.current;
    if (typingStopTimerRef.current) window.clearTimeout(typingStopTimerRef.current);
    typingLastEmitRef.current = 0;
    if (typingActiveRef.current && s && id) {
      typingActiveRef.current = false;
      s.emit('ticket:typing_stop', { ticketId: id });
    }
  };
  // Контекст-меню по ПКМ на сообщении и in-place редактирование.
  // Раньше клиент не мог редактировать/удалять свои сообщения с хелп-сайта —
  // эндпоинты добавлены, теперь привязываем к UI.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState('');
  const [menuState, setMenuState] = useState<{
    commentId: string;
    content: string;
    x: number;
    y: number;
  } | null>(null);
  const [rating, setRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  // Оценка показывается модалкой по центру экрана (а не тостом в углу).
  const [showRateModal, setShowRateModal] = useState(false);
  // Чтобы не открывать модалку снова после того как клиент закрыл её «Позже».
  const [rateModalDismissed, setRateModalDismissed] = useState(false);
  const [infoExpanded, setInfoExpanded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => ticketsApi.getById(id!),
    enabled: !!id,
    // 5 секунд + поллинг в фоне + обновление при возврате фокуса. Без этого
    // клиент не видит ответа оператора, пока не дёрнет F5.
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });

  const ticket: Ticket | undefined = data?.ticket;
  const comments: TicketComment[] = data?.comments || [];
  // Ответила ли уже поддержка (staff). Пока нет — в треде живой статус
  // «обращение принято, ждём ответа» вместо пустой дыры.
  const hasStaffReply = comments.some(
    (c) => c.origin === 'staff' || (!c.origin && !!c.author?.role && c.author.role !== 'client'),
  );

  useEffect(() => {
    if (comments.length === 0) return;
    // Скроллим только сам тред (своя «прокрутка чата»), а не страницу.
    // requestAnimationFrame — даём DOM применить layout с новым пузырьком.
    const el = threadRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [comments.length]);

  const addCommentMutation = useMutation({
    mutationFn: async () => {
      // 1) Сначала льём файлы — получаем их id'шники. Раньше делали
      //    наоборот: addComment → потом upload — файлы при этом цеплялись
      //    к тикету целиком, а не к КОММЕНТАРИЮ, и не показывались рядом
      //    с текстом сообщения («не прикрепляется» — глазами юзера).
      const attachmentIds: string[] = [];
      for (const file of replyFiles) {
        try {
          const att = await ticketsApi.uploadAttachment(id!, file);
          attachmentIds.push(att.id);
        } catch (err) {
          toast.error(`Не удалось загрузить: ${file.name}`);
          throw err; // прерываем — сообщение без файла не пускаем
        }
      }
      // 2) Сервер не принимает пустой content (z.string().min(1)). Если
      //    юзер прислал только вложения без текста — подставляем
      //    placeholder (как admin-app).
      const content =
        replyText.trim() || (attachmentIds.length > 0 ? '(вложение)' : '');
      // 3) Комментарий + ссылки на залитые файлы. Сервер сам перепривяжет
      //    Attachment.commentId с null на comment.id.
      const comment = await ticketsApi.addComment(id!, {
        content,
        attachmentIds: attachmentIds.length ? attachmentIds : undefined,
      });
      return comment;
    },
    onSuccess: () => {
      setReplyText('');
      setReplyFiles([]);
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      toast.success('Сообщение отправлено');
    },
    onError: (err: any) => {
      // Показываем реальный текст ошибки бэкенда (409/403 — «уже в работе у другого оператора»,
      // «нет доступа» и т.п.) вместо generic'а — юзер видит причину и понимает, что делать.
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Не удалось отправить сообщение';
      toast.error(msg);
    },
  });

  const deleteAttachmentMutation = useMutation({
    mutationFn: (attachmentId: string) => ticketsApi.deleteAttachment(id!, attachmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      toast.success('Вложение удалено');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || 'Не удалось удалить вложение';
      toast.error(msg);
    },
  });

  const editCommentMutation = useMutation({
    mutationFn: (vars: { commentId: string; content: string }) =>
      ticketsApi.updateComment(id!, vars.commentId, vars.content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      setEditingId(null);
      setEditingDraft('');
      toast.success('Сообщение обновлено');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || 'Не удалось сохранить';
      toast.error(msg);
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: string) => ticketsApi.deleteComment(id!, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      toast.success('Сообщение удалено');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || 'Не удалось удалить';
      toast.error(msg);
    },
  });

  // Закрытие контекст-меню по клику вне, Esc, или скроллу контейнера.
  useEffect(() => {
    if (!menuState) return;
    const close = () => setMenuState(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('click', close);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', close);
    };
  }, [menuState]);

  const rateMutation = useMutation({
    mutationFn: () =>
      ticketsApi.rate(id!, { rating, comment: ratingComment || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      setShowRateModal(false);
      toast.success('Спасибо за оценку!');
    },
    onError: () => {
      toast.error('Не удалось отправить оценку');
    },
  });

  const clientResolveMutation = useMutation({
    mutationFn: () => ticketsApi.clientResolve(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      setRateModalDismissed(false);
      setShowRateModal(true);
      toast.success('Обращение завершено');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || 'Не удалось завершить обращение';
      toast.error(msg);
    },
  });

  // Авто-открытие модалки оценки: тикет решён ИЛИ закрыт оператором и ещё
  // не оценён. Поэтому даже если оператор сам закрыл тикет, клиент зайдёт
  // и сразу увидит запрос оценки по центру. Не дёргаем повторно, если
  // клиент уже закрыл модалку «Позже».
  useEffect(() => {
    if (!ticket) return;
    const rateable =
      (ticket.status === 'resolved' || ticket.status === 'closed') && !ticket.rating;
    if (rateable && !rateModalDismissed) setShowRateModal(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.status, ticket?.rating]);

  const handleSendReply = (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyText.trim() && replyFiles.length === 0) return;
    emitTypingStop();
    addCommentMutation.mutate();
  };

  const handleFileAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Общий пайплайн в acceptFiles: лимит по размеру (25 МБ = multer'ом
    // на бэке) и по количеству (10 файлов). Раньше эта же логика жила
    // здесь inline — вынес, чтобы Ctrl+V через тот же путь ходил.
    if (e.target.files) acceptFiles(Array.from(e.target.files));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeReplyFile = (index: number) => {
    setReplyFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Общий acceptor для добавления File[] в список вложений.
  // Используется и picker'ом, и paste-обработчиком textarea (Ctrl+V
  // изображения из «Ножниц»/Skype/Telegram). Раньше был только в
  // handleFileAdd, привязанный к <input type=file>.
  const acceptFiles = (raw: File[]) => {
    const MAX_MB = 25;
    const MAX_FILES = 10;
    const tooBig: string[] = [];
    const ok = raw.filter((f) => {
      if (f.size > MAX_MB * 1024 * 1024) {
        tooBig.push(f.name || 'файл');
        return false;
      }
      return true;
    });
    if (tooBig.length > 0) {
      toast.error(
        tooBig.length === 1
          ? `Файл «${tooBig[0]}» больше ${MAX_MB} МБ`
          : `Файлы больше ${MAX_MB} МБ: ${tooBig.join(', ')}`,
      );
    }
    if (ok.length === 0) return;
    setReplyFiles((prev) => {
      const merged = [...prev, ...ok];
      if (merged.length > MAX_FILES) toast.error(`Максимум ${MAX_FILES} файлов`);
      return merged.slice(0, MAX_FILES);
    });
  };

  const isImage = (mimeType: string) => mimeType.startsWith('image/');

  if (isLoading) {
    return <LoadingSpinner fullPage text="Загрузка обращения..." />;
  }

  if (error || !ticket) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center px-4">
        <AlertCircle className="mb-4 h-12 w-12 text-[#fb7185]" />
        <h2 className="cc-display text-lg font-bold text-[#e7eaf3]">Обращение не найдено</h2>
        <p className="mt-1 text-sm text-[#9aa3bd]">Возможно, обращение было удалено или у вас нет доступа.</p>
        <Button
          variant="primary"
          className="mt-6"
          onClick={() => navigate('/tickets')}
          icon={<ArrowLeft className="h-4 w-4" />}
        >
          К списку обращений
        </Button>
      </div>
    );
  }

  const isClosed = ticket.status === 'closed';
  const isResolved = ticket.status === 'resolved';
  // Оценку можно поставить и когда оператор сам ЗАКРЫЛ тикет (closed),
  // не только когда клиент завершил его сам (resolved). Бэкенд rate это
  // уже разрешает (resolved|closed).
  const canRate = (isResolved || isClosed) && !ticket.rating;

  // Info sidebar content (shared between desktop sidebar and mobile collapsible)
  const infoContent = (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <Tag className="mt-0.5 h-4 w-4 text-[#727b96]" />
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#727b96]">Категория</p>
          <p className="text-sm font-medium text-[#e7eaf3]">
            {getCategoryLabel(ticket.category)}
          </p>
        </div>
      </div>

      {ticket.clubName && (
        <div className="flex items-start gap-3">
          <MapPin className="mt-0.5 h-4 w-4 text-[#727b96]" />
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#727b96]">Клуб</p>
            <p className="text-sm font-medium text-[#e7eaf3]">{ticket.clubName}</p>
          </div>
        </div>
      )}

      {ticket.deviceName && (
        <div className="flex items-start gap-3">
          <Monitor className="mt-0.5 h-4 w-4 text-[#727b96]" />
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#727b96]">Устройство</p>
            <p className="text-sm font-medium text-[#e7eaf3]">{ticket.deviceName}</p>
          </div>
        </div>
      )}

      {ticket.softwareVersion && (
        <div className="flex items-start gap-3">
          <Tag className="mt-0.5 h-4 w-4 text-[#727b96]" />
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#727b96]">Версия софта</p>
            <p className="text-sm font-medium text-[#e7eaf3]">{ticket.softwareVersion}</p>
          </div>
        </div>
      )}

      <div className="flex items-start gap-3">
        <CalendarDays className="mt-0.5 h-4 w-4 text-[#727b96]" />
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#727b96]">Создано</p>
          <p className="text-sm font-medium text-[#e7eaf3]">
            {format(new Date(ticket.createdAt), 'dd MMMM yyyy, HH:mm', { locale: ru })}
          </p>
        </div>
      </div>

      {ticket.slaDeadline && (
        <div className="flex items-start gap-3">
          <Clock className="mt-0.5 h-4 w-4 text-[#727b96]" />
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#727b96]">SLA дедлайн</p>
            <p className={clsx(
              'text-sm font-medium',
              new Date(ticket.slaDeadline) < new Date()
                ? 'text-[#fb7185]'
                : 'text-[#e7eaf3]'
            )}>
              {format(new Date(ticket.slaDeadline), 'dd MMMM yyyy, HH:mm', { locale: ru })}
            </p>
          </div>
        </div>
      )}

      {ticket.assignee && (
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full border border-line bg-fill-2 text-[9px] font-bold text-[#a5b4ff]">
            {ticket.assignee.name.charAt(0)}
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#727b96]">Ответственный</p>
            <p className="text-sm font-medium text-[#e7eaf3]">{ticket.assignee.name}</p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    // Страница — обычная колонка с естественным вертикальным скроллом.
    // Раньше была fixed-height (100dvh - 56px) + flex-1 на чате, из-за
    // чего короткий диалог оставлял огромную пустоту, а на закрытом
    // тикете плашки Resolution/Rating не помещались и обрезались.
    <div className="ticket-detail animate-fade-in flex flex-col">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <button
          onClick={() => navigate('/tickets')}
          className="mb-3 inline-flex items-center gap-1.5 text-sm text-[#9aa3bd] transition-colors hover:text-[#e7eaf3]"
        >
          <ArrowLeft className="h-4 w-4" />
          К списку обращений
        </button>
        {/* Компактная шапка одной строкой: № · заголовок · дата слева, бейджи
            справа. justify-between держит их прижатыми к краям карточки. */}
        <div className="cc-glass rounded-2xl px-4 py-3.5 md:px-5 md:py-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <span className="inline-flex flex-shrink-0 items-center gap-2 rounded-full border border-[rgba(147,160,255,.18)] bg-fill-2 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-[#bcc8ff]">
              #{ticket.number || '—'}
            </span>
            <div className="min-w-0 flex-1">
              <h1 className="cc-display truncate text-lg md:text-xl font-extrabold text-[#e7eaf3] leading-tight">
                {ticket.subject}
              </h1>
              <p className="mt-0.5 text-[11px] text-[#727b96] font-mono">
                Открыто {format(new Date(ticket.createdAt), 'd MMMM yyyy · HH:mm', { locale: ru })}
              </p>
            </div>
          </div>
          <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
            <Badge variant={getStatusBadgeVariant(ticket.status)}>
              {getStatusLabel(ticket.status)}
            </Badge>
            <Badge variant={getPriorityBadgeVariant(ticket.priority)} dot>
              {getPriorityLabel(ticket.priority)}
            </Badge>
          </div>
        </div>
      </div>

      {/* Mobile info collapsible */}
      <div className="mb-4 lg:hidden flex-shrink-0">
        <div className="cc-glass rounded-2xl p-4">
          <button
            onClick={() => setInfoExpanded(!infoExpanded)}
            className="flex w-full items-center justify-between text-left"
          >
            <h3 className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-[#727b96]">
              Информация
            </h3>
            {infoExpanded ? (
              <ChevronUp className="h-4 w-4 text-[#727b96]" />
            ) : (
              <ChevronDown className="h-4 w-4 text-[#727b96]" />
            )}
          </button>
          {infoExpanded && <div className="mt-4">{infoContent}</div>}
        </div>
      </div>

      <div className="grid gap-4 md:gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        {/* Чат — фиксированное окно с внутренним скроллом сообщений.
            Не тянется на всю страницу: даже с одним сообщением карточка
            остаётся компактной, а не разъезжается на полэкрана. */}
        <div className="flex flex-col gap-4">
          <div className="cc-glass flex flex-col h-[calc(100vh-260px)] min-h-[420px] max-h-[720px] overflow-hidden">
          {/* Шапка диалога — как в мессенджере: собеседник + статус. Заполняет
              верх карточки, чтобы пространство над сообщениями не читалось пустым. */}
          <div className="cc-chat-header flex-shrink-0">
            <div className="cc-chat-header__avatar">
              <Headphones className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="cc-chat-header__title">Поддержка Club:Core</div>
              <div className="cc-chat-header__status">
                <span className="cc-chat-header__dot" />
                На связи · отвечаем по SLA
              </div>
            </div>
            {ticket.number && (
              <span className="cc-chat-header__badge">#{ticket.number}</span>
            )}
          </div>
          {/* Тред сообщений: короткий диалог прижат к низу (спейсер), длинный
              скроллится. «Обои» + разделитель начала. */}
          <div
            ref={threadRef}
            className="cc-chat-thread flex flex-col flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 md:p-5"
          >
            {/* Сообщения идут от верха (раньше flex-1 спейсер прижимал
                короткий диалог к низу — оставался огромный пустой провал). */}
            <div className="cc-chat-thread__start">
              Начало обращения · {format(new Date(ticket.createdAt), 'd MMMM yyyy', { locale: ru })}
            </div>
            <div>
            {(() => {
              const hasTicketAttachments =
                Array.isArray(ticket.attachments) && ticket.attachments.length > 0;
              const messages: TicketComment[] = [...comments];
              if (hasTicketAttachments) {
                const attachmentMessage: TicketComment = {
                  id: 'ticket-attachments',
                  content: '',
                  isInternal: false,
                  origin: 'client',
                  author: {
                    id: (ticket as any).createdBy?.id || (ticket as any).client?.id || 'origin',
                    name:
                      (ticket as any).createdBy?.name ||
                      (ticket as any).client?.contactName ||
                      (ticket as any).client?.companyName ||
                      'Вы',
                    role: 'client',
                  },
                  attachments: ((ticket.attachments as any) || []).map((att: any) => ({
                    id: att.id,
                    filename: att.filename || att.fileName || att.originalName || 'file',
                    originalName: att.originalName || att.fileName || att.filename || 'Файл',
                    mimeType: att.mimeType || 'application/octet-stream',
                    size: att.size ?? att.fileSize ?? 0,
                    url: att.url || (att.storageKey ? att.storageKey.replace(/^\/app/, '') : ''),
                  })),
                  createdAt: ticket.createdAt,
                };
                messages.unshift(attachmentMessage);
              }
              // Описание тикета — первый пузырь клиента. Синтетический id
              // (не-UUID) → контекст-меню на нём не открывается.
              if (ticket.description && ticket.description.trim()) {
                messages.unshift({
                  id: 'ticket-description',
                  content: ticket.description,
                  isInternal: false,
                  origin: 'client',
                  author: {
                    id: (ticket as any).createdBy?.id || (ticket as any).client?.id || 'origin',
                    name:
                      (ticket as any).createdBy?.name ||
                      (ticket as any).client?.contactName ||
                      (ticket as any).client?.companyName ||
                      'Вы',
                    role: 'client',
                  },
                  attachments: [],
                  createdAt: ticket.createdAt,
                } as TicketComment);
              }
              return messages.sort(
                (a, b) => +new Date(a.createdAt) - +new Date(b.createdAt),
              );
            })().map((comment: TicketComment, idx: number, arr: TicketComment[]) => {
              // Автоответы бота (support-greeting@system.local и подобные
              // service-юзеры) в клиентском портале рендерятся отдельной
              // центральной system-плашкой — иначе визуально сливаются с
              // ответом живого оператора, и чат перестаёт читаться как
              // мессенджер. К ним не применяем группировку/контекст-меню.
              if (comment.author.isSystem) {
                return (
                  <div key={comment.id} className="my-3 flex justify-center">
                    <div className="max-w-[85%] rounded-full border border-white/[0.08] bg-white/[0.03] px-3.5 py-1.5 text-[12px] text-[#9aa3bd]">
                      {comment.content}
                    </div>
                  </div>
                );
              }

              // Alignment is driven strictly by `origin`: client-portal messages
              // on the right (mine), staff-portal on the left (support reply).
              // Legacy rows without origin fall back to role / current user id.
              const ownMessage =
                comment.origin === 'client' ||
                (!comment.origin &&
                  (comment.author.role === 'client' || comment.author.id === user?.id));
              const alignRight = ownMessage;

              // Группировка Telegram-style: соседние сообщения одного и того же
              // отправителя, разошедшиеся во времени не более чем на 5 минут,
              // склеиваются в один блок — БЕЗ повторного аватара/имени и с
              // плотным вертикальным зазором. Радиус угла у «внутренних»
              // сообщений сглаживается со стороны aвтара, чтобы визуально
              // образовалась цепочка bubble'ов.
              const prev: TicketComment | undefined = arr[idx - 1];
              const next: TicketComment | undefined = arr[idx + 1];
              const sameSender = (a?: TicketComment, b?: TicketComment) => {
                if (!a || !b) return false;
                const aOwn =
                  a.origin === 'client' ||
                  (!a.origin && (a.author.role === 'client' || a.author.id === user?.id));
                const bOwn =
                  b.origin === 'client' ||
                  (!b.origin && (b.author.role === 'client' || b.author.id === user?.id));
                if (aOwn !== bOwn) return false;
                // Разные операторы (по имени) не склеиваем.
                return (a.author.name || '') === (b.author.name || '');
              };
              const closeInTime = (a?: TicketComment, b?: TicketComment) => {
                if (!a || !b) return false;
                return Math.abs(+new Date(b.createdAt) - +new Date(a.createdAt)) < 5 * 60 * 1000;
              };
              const isGroupStart = !prev || !sameSender(prev, comment) || !closeInTime(prev, comment);
              const isGroupEnd = !next || !sameSender(next, comment) || !closeInTime(next, comment);

              return (
                <div
                  key={comment.id}
                  className={clsx(
                    'flex items-end gap-2.5',
                    isGroupStart ? 'mt-3' : 'mt-0.5',
                    alignRight ? 'flex-row-reverse' : 'flex-row',
                  )}
                >
                  {/* Аватар — только на ПОСЛЕДНЕМ сообщении в группе, иначе
                      место сохраняется placeholder'ом чтобы bubble не «прыгал». */}
                  {isGroupEnd ? (
                    <div
                      className={clsx(
                        'flex h-9 w-9 md:h-10 md:w-10 flex-shrink-0 items-center justify-center rounded-full text-[12px] font-bold shadow-sm',
                        alignRight
                          ? 'bg-gradient-to-br from-[#5b7cff] to-[#8b5cf6] text-white'
                          : 'bg-gradient-to-br from-[#1f2537] to-[#2a3452] text-[#a5b4ff] border border-line',
                      )}
                    >
                      {comment.author.name?.charAt(0)?.toUpperCase() || (
                        <UserIcon className="h-4 w-4 text-[#9aa3bd]" />
                      )}
                    </div>
                  ) : (
                    <div className="h-9 w-9 md:h-10 md:w-10 flex-shrink-0" aria-hidden />
                  )}

                  {/* Header + bubble */}
                  <div className="min-w-0 max-w-[85%] md:max-w-[68%]">
                    {isGroupStart && (
                      <div
                        className={clsx(
                          'mb-1 px-1 flex items-center gap-1.5 flex-wrap text-[11px] text-[#727b96]',
                          alignRight ? 'justify-end' : 'justify-start',
                        )}
                      >
                        <span className="font-semibold text-[#c3cbe0]">{comment.author.name}</span>
                        {!ownMessage && comment.author.role && getStaffRoleLabel(comment.author.role) && (
                          <span className="text-[10px] text-[#bcc8ff] bg-[rgba(91,124,255,.15)] border border-[rgba(91,124,255,.35)] rounded px-1.5 py-0.5 leading-none">
                            {getStaffRoleLabel(comment.author.role)}
                          </span>
                        )}
                        <span className="font-mono text-[#727b96]">
                          {format(new Date(comment.createdAt), 'HH:mm', { locale: ru })}
                        </span>
                      </div>
                    )}
                    <div className={clsx('flex', alignRight ? 'justify-end' : 'justify-start')}>
                      <div
                        className={clsx(
                          'px-4 py-2.5 md:px-5 md:py-3 text-[14.5px] leading-[1.55] whitespace-pre-wrap break-words',
                          'w-fit max-w-full',
                          // Base rounded — потом «сплющиваем» угол на стороне
                          // аватара для «внутренних» и «стартовых» сообщений.
                          'rounded-2xl',
                          alignRight
                            ? [
                                'bg-[#5b7cff] text-white',
                                !isGroupStart && 'rounded-tr-md',
                                !isGroupEnd && 'rounded-br-md',
                              ]
                            : [
                                'bg-[rgba(255,255,255,0.06)] text-[#e7eaf3] border border-white/[0.08]',
                                !isGroupStart && 'rounded-tl-md',
                                !isGroupEnd && 'rounded-bl-md',
                              ],
                          ownMessage && 'cursor-context-menu',
                        )}
                        onContextMenu={(e) => {
                          // Меню только для своих реальных сообщений. Синтетическое
                          // «ticket-attachments» (вложения без комментария) не имеет
                          // UUID — DELETE по нему даст 404.
                          const isSynthetic = !comment.id || !/^[0-9a-f-]{36}$/i.test(comment.id);
                          if (!ownMessage || isSynthetic) return;
                          e.preventDefault();
                          setMenuState({
                            commentId: comment.id,
                            content: comment.content,
                            x: e.clientX,
                            y: e.clientY,
                          });
                        }}
                      >
                        {editingId === comment.id ? (
                          <div onClick={(e) => e.stopPropagation()} className="min-w-[240px]">
                            <textarea
                              autoFocus
                              value={editingDraft}
                              onChange={(e) => setEditingDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  const trimmed = editingDraft.trim();
                                  if (trimmed && trimmed !== comment.content) {
                                    editCommentMutation.mutate({ commentId: comment.id, content: trimmed });
                                  }
                                }
                                if (e.key === 'Escape') {
                                  setEditingId(null);
                                  setEditingDraft('');
                                }
                              }}
                              rows={Math.min(8, Math.max(2, editingDraft.split('\n').length))}
                              className="w-full resize-none rounded-md border border-line bg-fill-2 px-3 py-2 text-sm text-[#e7eaf3] outline-none focus:ring-2 focus:ring-[rgba(91,124,255,.25)] focus:border-[rgba(91,124,255,.4)]"
                            />
                            <div className="mt-2 flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingId(null);
                                  setEditingDraft('');
                                }}
                                className="rounded-md px-3 py-1 text-xs text-[#9aa3bd] hover:bg-fill-2"
                              >
                                Отмена
                              </button>
                              <button
                                type="button"
                                disabled={
                                  !editingDraft.trim() ||
                                  editingDraft.trim() === comment.content ||
                                  editCommentMutation.isPending
                                }
                                onClick={() => {
                                  const trimmed = editingDraft.trim();
                                  if (trimmed && trimmed !== comment.content) {
                                    editCommentMutation.mutate({
                                      commentId: comment.id,
                                      content: trimmed,
                                    });
                                  }
                                }}
                                className="cc-accent-grad cc-cta rounded-md px-3 py-1 text-xs font-medium text-accent-text disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                Сохранить
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {/* Hide "(вложение)" placeholder when attachments are present */}
                            {!(comment.content === '(вложение)' && comment.attachments && comment.attachments.length > 0) && comment.content}
                            {comment.updatedAt &&
                              comment.createdAt &&
                              new Date(comment.updatedAt).getTime() - new Date(comment.createdAt).getTime() > 2000 && (
                                <span className="ml-1 text-[10px] italic text-[#727b96]">(изм.)</span>
                              )}
                          </>
                        )}

                        {/* Attachments */}
                        {comment.attachments && comment.attachments.length > 0 && (
                          <div className="mt-2 space-y-1.5">
                            {comment.attachments.map((att: Attachment) => (
                              <div key={att.id} className="group/att relative inline-block">
                                {isImage(att.mimeType) ? (
                                  <a
                                    href={att.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block"
                                  >
                                    <img
                                      src={att.url}
                                      alt={att.originalName}
                                      className="max-h-80 md:max-h-[28rem] rounded-lg border border-line"
                                    />
                                  </a>
                                ) : (
                                  <a
                                    href={att.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 rounded-lg border border-line px-3 py-1.5 text-xs font-medium bg-fill-2 text-[#9aa3bd] hover:bg-fill-2 transition-colors"
                                  >
                                    <Download className="h-3.5 w-3.5" />
                                    <span className="max-w-[150px] md:max-w-none truncate">
                                      {att.originalName}
                                    </span>
                                    <span className="opacity-60">
                                      ({(att.size / 1024).toFixed(0)} КБ)
                                    </span>
                                  </a>
                                )}
                                {ownMessage && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (window.confirm('Удалить вложение?')) {
                                        deleteAttachmentMutation.mutate(att.id);
                                      }
                                    }}
                                    className="absolute -top-1.5 -right-1.5 hidden group-hover/att:flex h-5 w-5 items-center justify-center rounded-full bg-[#fb7185] text-fg-primary text-[10px] shadow hover:brightness-110 transition-all"
                                    title="Удалить вложение"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Живой статус вместо пустоты: пока поддержка не ответила и
                тикет открыт — бабл «обращение принято, специалист скоро
                ответит» с анимированными точками. */}
            {!hasStaffReply && !isResolved && !isClosed && (
              <div className="flex items-end gap-2">
                <div className="flex h-8 w-8 md:h-9 md:w-9 flex-shrink-0 items-center justify-center rounded-full border border-line bg-[rgba(91,124,255,.12)] text-[#a5b4ff]">
                  <Headphones className="h-4 w-4" />
                </div>
                <div className="min-w-0 max-w-[85%] md:max-w-[72%]">
                  <div className="mb-1 text-[11px] text-[#727b96]">
                    <span className="font-semibold text-[#9aa3bd]">Поддержка Club:Core</span>
                  </div>
                  <div className="w-fit rounded-[14px] rounded-bl-sm border border-line bg-fill-2 px-3.5 py-2.5 text-sm text-[#9aa3bd]">
                    <span className="flex items-center gap-2">
                      <span className="flex gap-1" aria-hidden="true">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#a5b4ff] animate-bounce [animation-delay:-0.2s]" />
                        <span className="h-1.5 w-1.5 rounded-full bg-[#a5b4ff] animate-bounce [animation-delay:-0.1s]" />
                        <span className="h-1.5 w-1.5 rounded-full bg-[#a5b4ff] animate-bounce" />
                      </span>
                      Обращение принято — специалист скоро ответит
                    </span>
                  </div>
                </div>
              </div>
            )}
            </div>
            <div ref={messagesEndRef} />
          </div>

          {/* Floating context menu (правый клик на своём сообщении).
              Корневой обработчик закрытия (window click) ловит клик ВНУТРИ меню,
              поэтому останавливаем propagation на самом блоке. */}
          {menuState && (
            <div
              role="menu"
              className="cc-glass fixed z-50 min-w-[200px] rounded-xl border border-line py-1 text-sm shadow-xl"
              style={{
                // Прижимаем в правом/нижнем краях, чтобы меню не уезжало за viewport.
                left: Math.min(menuState.x, window.innerWidth - 220),
                top: Math.min(menuState.y, window.innerHeight - 160),
              }}
              onClick={(e) => e.stopPropagation()}
              onContextMenu={(e) => e.preventDefault()}
            >
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-[#e7eaf3] hover:bg-fill-2"
                onClick={() => {
                  if (menuState.content) {
                    navigator.clipboard
                      ?.writeText(menuState.content)
                      .then(() => toast.success('Скопировано'))
                      .catch(() => toast.error('Не удалось скопировать'));
                  }
                  setMenuState(null);
                }}
              >
                <Copy className="h-4 w-4" /> Копировать текст
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-[#e7eaf3] hover:bg-fill-2"
                onClick={() => {
                  setEditingId(menuState.commentId);
                  setEditingDraft(menuState.content);
                  setMenuState(null);
                }}
              >
                <Pencil className="h-4 w-4" /> Редактировать
              </button>
              <div className="my-1 border-t border-line" />
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-[#fb7185] hover:bg-[rgba(251,113,133,.1)]"
                onClick={() => {
                  const target = menuState;
                  setMenuState(null);
                  if (window.confirm('Удалить сообщение? Действие необратимо.')) {
                    deleteCommentMutation.mutate(target.commentId);
                  }
                }}
              >
                <Trash2 className="h-4 w-4" /> Удалить
              </button>
            </div>
          )}

          {/* Resolution card */}
          {(isResolved || isClosed) && ticket?.resolutionNote && (
            <div className="cc-glass rounded-2xl px-4 py-4 md:px-5 md:py-4 flex-shrink-0">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#34d399]" />
                <div className="min-w-0 flex-1">
                  <p className="cc-display text-sm font-bold text-[#e7eaf3]">Обращение завершено</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-[#9aa3bd]">
                    <span className="text-[#727b96]">Причина: </span>
                    {ticket.resolutionNote}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Closed notice */}
          {isClosed && (
            <div className="cc-glass rounded-2xl p-4">
              <div className="flex items-center gap-3 text-[#9aa3bd]">
                <Lock className="h-5 w-5" />
                <div>
                  <p className="text-sm font-medium text-[#e7eaf3]">Тикет закрыт</p>
                  <p className="text-xs text-[#727b96]">
                    Обращение было закрыто{' '}
                    {ticket.closedAt
                      ? format(new Date(ticket.closedAt), 'dd MMMM yyyy', { locale: ru })
                      : ''}
                    . Если проблема повторяется, создайте новое обращение.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Rating form for resolved tickets */}
          {canRate && (
            <div className="cc-glass rounded-2xl p-4 md:p-5 flex-shrink-0">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#34d399]" />
                <div className="flex-1">
                  <h3 className="cc-display text-sm font-bold text-[#e7eaf3]">
                    Обращение решено! Оцените качество поддержки
                  </h3>
                  <p className="mt-1 text-xs text-[#727b96]">
                    Ваша оценка поможет нам улучшить качество обслуживания
                  </p>

                  <div className="mt-4">
                    <StarRating value={rating} onChange={setRating} size="lg" />
                  </div>

                  {rating > 0 && (
                    <div className="mt-3">
                      <textarea
                        value={ratingComment}
                        onChange={(e) => setRatingComment(e.target.value)}
                        placeholder="Оставьте комментарий (необязательно)"
                        className="block w-full resize-none rounded-md border border-line bg-fill-2 px-3 py-2 text-sm text-[#e7eaf3] placeholder:text-[#727b96] focus:outline-none focus:ring-2 focus:ring-[rgba(91,124,255,.25)] focus:border-[rgba(91,124,255,.4)]"
                        rows={2}
                      />
                      <Button
                        variant="primary"
                        size="sm"
                        className="mt-2"
                        onClick={() => rateMutation.mutate()}
                        loading={rateMutation.isPending}
                      >
                        Отправить оценку
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Show existing rating */}
          {ticket.rating && (
            <div className="cc-glass rounded-2xl p-4 flex-shrink-0">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-[#34d399]" />
                <div>
                  <p className="cc-display text-sm font-bold text-[#e7eaf3]">Ваша оценка</p>
                  <StarRating value={ticket.rating} readonly size="sm" className="mt-1" />
                  {ticket.ratingComment && (
                    <p className="mt-1 text-xs text-[#9aa3bd]">{ticket.ratingComment}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Блок «Проблема решена? → Завершить обращение» убран по
              требованию: клиент не закрывает тикет сам, статус «решён»
              ставит только оператор поддержки. clientResolveMutation
              оставлен — может пригодиться позже, dead-code в UI. */}

          {/* Reply form */}
          {!isClosed && (
            <div className="border-t border-line bg-[color:var(--fill-1,rgba(127,140,180,0.04))] p-3 md:p-4 flex-shrink-0">
              {operatorTyping && (
                <div className="cc-typing">
                  <span className="cc-typing-dots"><i /><i /><i /></span>
                  Оператор печатает…
                </div>
              )}
              <form onSubmit={handleSendReply}>
                <textarea
                  value={replyText}
                  onChange={(e) => { setReplyText(e.target.value); emitTyping(); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendReply(e as unknown as React.FormEvent);
                    }
                  }}
                  onPaste={(e) => {
                    // Ctrl+V картинок/файлов из буфера → в список
                    // вложений. Без этого скрин из «Ножниц» вставлялся
                    // в тело письма как base64-текст и не отправлялся.
                    const items = e.clipboardData?.items;
                    if (!items || items.length === 0) return;
                    const pasted: File[] = [];
                    for (const it of Array.from(items)) {
                      if (it.kind !== 'file') continue;
                      const f = it.getAsFile();
                      if (!f) continue;
                      // Скриншотам браузер даёт name="image.png";
                      // делаем уникальное имя, чтобы файлы не сливались
                      // в списке и на сервере.
                      const fname = f.name && f.name !== 'image.png'
                        ? f.name
                        : `paste-${Date.now()}-${pasted.length}.${(f.type.split('/')[1] || 'png').replace(/[^a-z0-9]/gi, '')}`;
                      pasted.push(
                        f.name === fname ? f : new File([f], fname, { type: f.type }),
                      );
                    }
                    if (pasted.length === 0) return;
                    e.preventDefault();
                    acceptFiles(pasted);
                  }}
                  placeholder="Введите сообщение... (Enter — отправить, Shift+Enter — новая строка, вложения — Ctrl+V)"
                  className="block w-full resize-none rounded-md border border-line bg-fill-2 px-3 py-2.5 text-sm text-[#e7eaf3] placeholder:text-[#727b96] focus:outline-none focus:ring-2 focus:ring-[rgba(91,124,255,.25)] focus:border-[rgba(91,124,255,.4)]"
                  rows={3}
                />

                {/* Reply file attachments */}
                {replyFiles.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {replyFiles.map((file, idx) => (
                      <div
                        key={`${file.name}-${idx}`}
                        className="flex items-center gap-1.5 rounded-lg border border-line bg-fill-2 px-2.5 py-1.5 text-xs"
                      >
                        {file.type.startsWith('image/') ? (
                          <ImageIcon className="h-3.5 w-3.5 text-[#a5b4ff]" />
                        ) : (
                          <FileIcon className="h-3.5 w-3.5 text-[#727b96]" />
                        )}
                        <span className="max-w-[80px] md:max-w-[120px] truncate text-[#9aa3bd]">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeReplyFile(idx)}
                          className="ml-0.5 rounded-sm p-0.5 text-[#727b96] hover:bg-fill-2 hover:text-[#e7eaf3]"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-3 flex items-center justify-between">
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileAdd}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-fill-2 px-2 md:px-3 py-1.5 text-sm text-[#9aa3bd] transition-colors hover:bg-fill-2 hover:text-[#e7eaf3]"
                    >
                      <Paperclip className="h-4 w-4" />
                      <span className="hidden sm:inline">Прикрепить файл</span>
                    </button>
                  </div>
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    loading={addCommentMutation.isPending}
                    disabled={!replyText.trim() && replyFiles.length === 0}
                    icon={<Send className="h-3.5 w-3.5" />}
                    className="cc-accent-grad cc-cta text-accent-text"
                  >
                    Отправить
                  </Button>
                </div>
              </form>
            </div>
          )}
          </div>
        </div>

        {/* Info sidebar - desktop only: единая карточка с секциями Статус /
            Детали / Помощь через тонкие разделители. Раньше это были три
            отдельные cc-glass плитки — визуально дробили правую колонку. */}
        <aside className="hidden lg:flex lg:flex-col gap-4">
          <div className="cc-glass rounded-2xl divide-y divide-line">
          {/* Статус + прогресс */}
          <div className="p-5">
            <h3 className="mb-4 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-[#727b96]">
              Статус
            </h3>
            {(() => {
              const lifecycle = [
                { key: 'new', label: 'Создано' },
                { key: 'in_progress', label: 'В работе' },
                { key: 'resolved', label: 'Решено' },
                { key: 'closed', label: 'Закрыто' },
              ];
              const idx =
                (
                  {
                    new: 0,
                    open: 0,
                    in_progress: 1,
                    waiting_client: 1,
                    waiting_specialist: 1,
                    escalated: 1,
                    reopened: 1,
                    resolved: 2,
                    closed: 3,
                  } as Record<string, number>
                )[ticket.status] ?? 0;
              return (
                <ol>
                  {lifecycle.map((s, i) => {
                    const done = i < idx;
                    const current = i === idx;
                    return (
                      <li key={s.key} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <span
                            className={clsx(
                              'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border text-[10px] font-bold',
                              current
                                ? 'border-[rgba(91,124,255,.5)] bg-[rgba(91,124,255,.18)] text-[#a5b4ff]'
                                : done
                                  ? 'border-[rgba(52,211,153,.5)] bg-[rgba(52,211,153,.15)] text-[#34d399]'
                                  : 'border-line bg-fill-2 text-[#727b96]',
                            )}
                          >
                            {done ? '✓' : i + 1}
                          </span>
                          {i < lifecycle.length - 1 && (
                            <span
                              className={clsx('my-1 w-px flex-1', done ? 'bg-[rgba(52,211,153,.4)]' : 'bg-line')}
                              style={{ minHeight: 16 }}
                            />
                          )}
                        </div>
                        <span
                          className={clsx(
                            'pb-3 pt-0.5 text-sm',
                            current
                              ? 'font-semibold text-[#e7eaf3]'
                              : done
                                ? 'text-[#9aa3bd]'
                                : 'text-[#727b96]',
                          )}
                        >
                          {s.label}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              );
            })()}
            {ticket.slaDeadline && (
              <div
                className={clsx(
                  'mt-1 flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm',
                  new Date(ticket.slaDeadline) < new Date()
                    ? 'border-[rgba(251,113,133,.3)] bg-[rgba(251,113,133,.08)] text-[#fb7185]'
                    : 'border-line bg-fill-2 text-[#9aa3bd]',
                )}
              >
                <Clock className="h-4 w-4 flex-shrink-0" />
                <span>SLA до {format(new Date(ticket.slaDeadline), 'dd MMM, HH:mm', { locale: ru })}</span>
              </div>
            )}
          </div>

          {/* Детали обращения */}
          <div className="p-5">
            <h3 className="mb-4 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-[#727b96]">
              Детали обращения
            </h3>
            {infoContent}
          </div>

          {/* Нужна помощь */}
          <div className="p-5">
            <h3 className="mb-3 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-[#727b96]">
              Нужна помощь?
            </h3>
            <p className="mb-3 text-sm leading-relaxed text-[#9aa3bd]">
              Найдите готовый ответ в базе знаний или свяжитесь с поддержкой напрямую.
            </p>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => navigate('/knowledge')}
                className="flex w-full items-center gap-2.5 rounded-xl border border-line bg-fill-2 px-3 py-2.5 text-sm font-medium text-[#e7eaf3] transition-colors hover:border-[rgba(91,124,255,.35)]"
              >
                <BookOpen className="h-4 w-4 text-[#a5b4ff]" /> База знаний
              </button>
              <button
                type="button"
                onClick={() => navigate('/contacts')}
                className="flex w-full items-center gap-2.5 rounded-xl border border-line bg-fill-2 px-3 py-2.5 text-sm font-medium text-[#e7eaf3] transition-colors hover:border-[rgba(91,124,255,.35)]"
              >
                <Phone className="h-4 w-4 text-[#a5b4ff]" /> Контакты
              </button>
            </div>
          </div>
          </div>
        </aside>
      </div>

      {/* Оценка качества — модалка по центру экрана. Появляется, когда тикет
          решён/закрыт и ещё не оценён (в т.ч. если оператор сам закрыл). */}
      {showRateModal && canRate && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm animate-fade-in"
          onClick={() => {
            setShowRateModal(false);
            setRateModalDismissed(true);
          }}
        >
          <div
            className="cc-glass relative w-full max-w-md rounded-2xl p-6 text-center shadow-2xl md:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                setShowRateModal(false);
                setRateModalDismissed(true);
              }}
              className="absolute right-4 top-4 text-[#727b96] transition-colors hover:text-[#e7eaf3]"
              aria-label="Закрыть"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[rgba(52,211,153,.12)]">
              <CheckCircle2 className="h-7 w-7 text-[#34d399]" />
            </div>
            <h3 className="cc-display text-lg font-bold text-[#e7eaf3]">Обращение решено!</h3>
            <p className="mt-1 text-sm text-[#9aa3bd]">Пожалуйста, оцените качество поддержки</p>

            <div className="mt-5 flex justify-center">
              <StarRating value={rating} onChange={setRating} size="lg" />
            </div>

            {rating > 0 && (
              <div className="mt-4 text-left">
                <textarea
                  value={ratingComment}
                  onChange={(e) => setRatingComment(e.target.value)}
                  placeholder="Оставьте комментарий (необязательно)"
                  className="block w-full resize-none rounded-md border border-line bg-fill-2 px-3 py-2 text-sm text-[#e7eaf3] placeholder:text-[#727b96] focus:border-[rgba(91,124,255,.4)] focus:outline-none focus:ring-2 focus:ring-[rgba(91,124,255,.25)]"
                  rows={3}
                />
              </div>
            )}

            <div className="mt-5 flex items-center justify-center gap-2">
              <Button
                variant="primary"
                onClick={() => rateMutation.mutate()}
                loading={rateMutation.isPending}
                disabled={rating === 0}
              >
                Отправить оценку
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowRateModal(false);
                  setRateModalDismissed(true);
                }}
              >
                Позже
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetailPage;
